import React, { useState, useCallback } from 'react'
import { StyleSheet, View } from 'react-native'
import { Text } from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import { useNodeTopUsersQuery } from '../../../../generated/graphql'
import { UserStatRow } from '../components/user-stats.row'
import { SortOption, SortOptions } from '../../../../@ui/buttons/sort.button'
import {
  ExportButton,
  ExportBanner,
} from '../../../../@ui/buttons/export.button'
import { useExportUsers } from '../hooks/export-users.hook'
import { Row, PaginatedFlatList } from '../../../../@ui'
import { TFunction } from 'i18next'

interface UserStatsTabProps {
  nodeId: string
  nodeName: string
}

const sortOptions = (
  t: TFunction<'translation', undefined>
): SortOption<string>[] => [
  {
    field: 'coRank',
    label: t('sort-highest-corank'),
    direction: 'DESC',
    icon: 'sort-numeric-ascending',
  },
  {
    field: 'coRank',
    label: t('sort-lowest-corank'),
    direction: 'ASC',
    icon: 'sort-numeric-descending',
  },
  {
    field: 'username',
    label: t('sort-name-a-z'),
    direction: 'ASC',
    icon: 'sort-alphabetical-ascending',
  },
  {
    field: 'username',
    label: t('sort-name-z-a'),
    direction: 'DESC',
    icon: 'sort-alphabetical-descending',
  },
]

export function UserStatsTab({ nodeId, nodeName }: UserStatsTabProps) {
  const { t } = useTranslation('nodes')
  const [limit] = useState(30)
  const [sortField, setSortField] = useState<'coRank' | 'username'>('coRank')
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC')

  const { exportUsers, isExporting, progress } = useExportUsers({
    nodeId,
    nodeName,
    sortBy: sortField,
    sort: sortDirection,
  })

  const { data, loading, refetch, error, fetchMore } = useNodeTopUsersQuery({
    variables: {
      input: {
        nodeId,
        limit,
        offset: 0,
        sortBy: sortField,
        sort: sortDirection,
      },
    },
  })

  const users = data?.nodeTopUsers?.users || []
  const total = data?.nodeTopUsers?.total || 0

  // Debug logging
  if (error) {
    console.error('User stats query error:', error)
  }
  if (data) {
    console.log('User stats data:', { total, usersCount: users.length })
  }

  const handleSortChange = useCallback(
    (field: string, direction: 'ASC' | 'DESC') => {
      setSortField(field as 'coRank' | 'username')
      setSortDirection(direction)
      // Refetch with new sort parameters
      refetch({
        input: {
          nodeId,
          limit,
          offset: 0,
          sortBy: field,
          sort: direction,
        },
      })
    },
    [refetch, nodeId, limit]
  )

  const handleLoadMore = useCallback(
    async (params: { offset: number; limit: number }) => {
      if (loading) return

      await fetchMore({
        variables: {
          input: {
            nodeId,
            limit: params.limit,
            offset: params.offset,
            sortBy: sortField,
            sort: sortDirection,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev
          return {
            nodeTopUsers: {
              ...fetchMoreResult.nodeTopUsers,
              users: [
                ...prev.nodeTopUsers.users,
                ...fetchMoreResult.nodeTopUsers.users,
              ],
            },
          }
        },
      })
    },
    [nodeId, loading, fetchMore, sortField, sortDirection]
  )

  const handleRefresh = useCallback(async () => {
    await refetch({
      input: {
        nodeId,
        limit,
        offset: 0,
        sortBy: sortField,
        sort: sortDirection,
      },
    })
  }, [refetch, nodeId, limit, sortField, sortDirection])

  return (
    <View style={styles.container}>
      <ExportBanner
        visible={isExporting}
        progress={progress}
        message={t('exporting-users')}
      />

      <View style={styles.header}>
        <Row justifyContent="space-between" alignItems="center">
          <Text variant="titleMedium">
            {t('user-stats')} ({total})
          </Text>
          <Row gap={1}>
            <ExportButton
              onPress={exportUsers}
              disabled={isExporting || !users.length}
              tooltip={t('export-users-to-csv')}
            />
            <SortOptions
              selectedField={sortField}
              selectedDirection={sortDirection}
              onSortChange={handleSortChange}
              options={sortOptions(t)}
            />
          </Row>
        </Row>
      </View>

      <PaginatedFlatList
        data={users}
        total={total}
        initialLimit={limit}
        style={styles.listContainer}
        contentContainerStyle={styles.listContent}
        renderItem={({ item, index }) => {
          const userWithCoRank = item as NonNullable<(typeof users)[0]>
          return (
            <UserStatRow
              userWithCoRank={userWithCoRank}
              rank={userWithCoRank.dbPosition || index + 1}
            />
          )
        }}
        keyExtractor={(item) => {
          const userWithCoRank = item as NonNullable<(typeof users)[0]>
          return userWithCoRank.user.id
        }}
        onLoadMore={handleLoadMore}
        onRefresh={handleRefresh}
        emptyMessage={t('no-users-found')}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  listContainer: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: 16,
    paddingTop: 8,
  },
})
